import dayjs from "dayjs";
import weekYear from "dayjs/plugin/weekYear";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekday from "dayjs/plugin/weekday";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

/** IANA timezone used for date-only fields (birthdate, etc.) across the app */
export const APP_TIMEZONE = "Europe/Prague";

dayjs.extend(utc);
dayjs.extend(timezone);

class DateUtils {
  DAYS = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
  DAYS_LONG = ["Neděle", "Pondělí", "Úterý", "Středa", "Čtvrtek", "Pátek", "Sobota"];
  WORKING_DAYS = new Set([1, 2, 3, 4, 5]);
  WORKING_HOURS_START = 9;
  WORKING_HOURS_END = 16; // inclusive of this hour

  MONTHS = [
    "Leden",
    "Únor",
    "Březen",
    "Duben",
    "Květen",
    "Červen",
    "Červenec",
    "Srpen",
    "Září",
    "Říjen",
    "Listopad",
    "Prosinec",
  ];

  setupDayJs() {
    dayjs.locale("cs");
    dayjs.extend(weekOfYear);
    dayjs.extend(weekYear);
    dayjs.extend(weekday);
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isSameOrAfter);
  }

  /**
   * Extracts calendar Y-M-D from a picker value (dayjs/Date) or from an ISO string
   * interpreted in {@link APP_TIMEZONE}.
   * @param {string|Date|import("dayjs").Dayjs} value
   * @returns {{ year: number, month: number, day: number }|null} month is 1-based
   */
  getCalendarDateParts(value) {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (dayjs.isDayjs(value) || (value && typeof value.year === "function" && typeof value.date === "function")) {
      if (typeof value.isValid === "function" && !value.isValid()) {
        return null;
      }
      return { year: value.year(), month: value.month() + 1, day: value.date() };
    }

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return { year: value.getFullYear(), month: value.getMonth() + 1, day: value.getDate() };
    }

    if (typeof value === "string") {
      const prague = dayjs.utc(value).tz(APP_TIMEZONE);
      if (!prague.isValid()) {
        return null;
      }
      return { year: prague.year(), month: prague.month() + 1, day: prague.date() };
    }

    return null;
  }

  /**
   * Converts a date-only value to an ISO string at midnight in {@link APP_TIMEZONE}.
   * Picker values use their displayed calendar day; ISO strings are interpreted in Prague.
   * @param {string|Date|import("dayjs").Dayjs} value
   * @returns {string|undefined}
   */
  toPragueMidnightISO(value) {
    const parts = this.getCalendarDateParts(value);
    if (!parts) {
      return undefined;
    }
    const { year, month, day } = parts;
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dayjs.tz(dateStr, APP_TIMEZONE).startOf("day").toISOString();
  }

  /**
   * Builds a local date-only dayjs for DatePicker from a stored instant, using the
   * calendar day in {@link APP_TIMEZONE} (avoids off-by-one outside Prague).
   * @param {string|Date|import("dayjs").Dayjs} value
   * @returns {import("dayjs").Dayjs|null}
   */
  toPragueDateOnly(value) {
    const parts = this.getCalendarDateParts(value);
    if (!parts) {
      return null;
    }
    const { year, month, day } = parts;
    const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const d = dayjs(dateStr);
    return d.isValid() ? d : null;
  }

  // returns monday to sunday range of selected week
  getAllWeeksStarts(leftDate, rightDate) {
    const weekStarts = [];
    let startOn = dayjs(leftDate).startOf("week");
    const endOn = dayjs(rightDate).add(7, "days").startOf("week");
    do {
      weekStarts.push(startOn);
      startOn = startOn.add(7, "days");
    } while (startOn < endOn);
    return weekStarts;
  }

  getAllMonthsStarts(leftDate, rightDate) {
    const monthStarts = [];
    let startOn = dayjs(leftDate).startOf("month");
    const endOn = dayjs(rightDate).add(1, "month").startOf("month");
    do {
      monthStarts.push(startOn);
      startOn = startOn.add(1, "month");
    } while (startOn < endOn);
    return monthStarts;
  }

  parseLocaleDate(date) {
    const [day, month, year] = date.split(".");
    return new Date(year, parseInt(month) - 1, day);
  }

  parseLocaleDateTime(localeDate) {
    const [date, time] = localeDate.split(" ");
    const [day, month, year] = date.split(".");
    const [hours, minutes, seconds] = time.split(":");
    return new Date(year, parseInt(month) - 1, day, hours, minutes, seconds || null);
  }

  translateDayLength(dayCount) {
    if (dayCount === 0 || (dayCount >= 5 && dayCount <= 30)) return `${dayCount} dní`;
    if (dayCount === 1) return `${dayCount} den`;
    if (dayCount >= 2 && dayCount <= 4) return `${dayCount} dny`;

    const monthCount = Math.floor(dayCount / 30);
    if (monthCount === 1) return `${monthCount} měsíc`;
    if (monthCount >= 2 && monthCount <= 4) return `${monthCount} měsíce`;
    return `${monthCount} měsíců`;
  }

  getDayDifference(from, to) {
    if (!from || !to) return 0;

    const dateFrom = new Date(from);
    const dateTo = new Date(to);
    return 1 + (dateTo - dateFrom) / (1000 * 60 * 60 * 24);
  }

  /**
   * Determine whether `now` is inside an `{ from, to }` interval (inclusive of `from`,
   * exclusive of `to`). Returns false for malformed intervals (missing `from` or `to`).
   *
   * @param {{ from?: string|Date, to?: string|Date } | null | undefined} interval
   * @param {Date} [now] - reference point; defaults to current time
   * @returns {boolean}
   */
  isWithinInterval(interval, now = new Date()) {
    if (!interval?.from || !interval?.to) return false;
    const from = new Date(interval.from);
    const to = new Date(interval.to);
    return now >= from && now < to;
  }
}

export default new DateUtils();
